import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import './App.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Generate a simple user ID for favorites
const USER_ID = localStorage.getItem('user_id') || (() => {
  const id = 'user_' + Math.random().toString(36).substr(2, 9);
  localStorage.setItem('user_id', id);
  return id;
})();

// Popular genres for quick filters
const POPULAR_GENRES = [
  'pop', 'rock', 'jazz', 'κλασική', 'ηλεκτρονική', 'λαϊκή', 'χιπ-χοπ', 
  'reggae', 'μπλουζ', 'παραδοσιακή', 'παγκόσμια μουσική', 'ειδήσεις', 'ομιλία', 'αθλητικά'
];

function App() {
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'dark');
  const [stations, setStations] = useState([]);
  const [countries, setCountries] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [tags, setTags] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentStation, setCurrentStation] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [showFavorites, setShowFavorites] = useState(false);
  const [volume, setVolume] = useState(0.7);
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [bitrateMin, setBitrateMin] = useState('');
  const [bitrateMax, setBitrateMax] = useState('');
  const [selectedCodec, setSelectedCodec] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('clickcount');
  const [selectedGenre, setSelectedGenre] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareStation, setShareStation] = useState(null);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalStations, setTotalStations] = useState(0);
  const [hasMoreStations, setHasMoreStations] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const audioRef = useRef(null);

  // SEO and Analytics tracking
  useEffect(() => {
    // Update page title dynamically based on search/filters
    let pageTitle = "AIPANTHEONGR-RADIO | 53.000+ Δωρεάν Παγκόσμιοι Ραδιοφωνικοί Σταθμοί";
    
    if (searchQuery) {
      pageTitle = `"${searchQuery}" - Αναζήτηση Ραδιοφωνικών Σταθμών | AIPANTHEONGR-RADIO`;
    } else if (selectedCountry) {
      pageTitle = `${selectedCountry} Ραδιοφωνικοί Σταθμοί | AIPANTHEONGR-RADIO`;
    } else if (selectedGenre) {
      pageTitle = `${selectedGenre} Μουσική Radio | AIPANTHEONGR-RADIO`;
    } else if (showFavorites) {
      pageTitle = `Αγαπημένοι Ραδιοφωνικοί Σταθμοί | AIPANTHEONGR-RADIO`;
    }
    
    document.title = pageTitle;
    
    // Update meta description dynamically
    let metaDescription = `Ακούστε 53.000+ παγκόσμιους ραδιοφωνικούς σταθμούς δωρεάν! Ελληνικό radio, διεθνή μουσική, ειδήσεις, ζωντανή μετάδοση.`;
    
    if (searchQuery) {
      metaDescription = `Αναζήτηση "${searchQuery}" - Βρείτε τους καλύτερους ραδιοφωνικούς σταθμούς στο AIPANTHEONGR-RADIO.`;
    } else if (selectedCountry) {
      metaDescription = `Ακούστε δωρεάν ραδιοφωνικούς σταθμούς από ${selectedCountry}. Ζωντανή μετάδοση, μουσική και ειδήσεις από ${selectedCountry}.`;
    }
    
    // Update meta description tag
    const metaDescTag = document.querySelector('meta[name="description"]');
    if (metaDescTag) {
      metaDescTag.setAttribute('content', metaDescription);
    }
    
    // Track page views for analytics
    if (window.posthog) {
      window.posthog.capture('page_view', {
        page_title: pageTitle,
        search_query: searchQuery,
        selected_country: selectedCountry,
        selected_genre: selectedGenre,
        showing_favorites: showFavorites
      });
    }
    
  }, [searchQuery, selectedCountry, selectedGenre, showFavorites]);

  // CRITICAL: Load initial data on component mount
  useEffect(() => {
    loadInitialData();
    // Load offline favorites
    const offlineFavorites = JSON.parse(localStorage.getItem('offline_favorites') || '[]');
    setFavorites(offlineFavorites);
  }, []);

  // Track user interactions for SEO insights
  const trackUserInteraction = (action, data = {}) => {
    if (window.posthog) {
      window.posthog.capture(`radio_${action}`, {
        ...data,
        timestamp: new Date().toISOString(),
        user_agent: navigator.userAgent,
        page_url: window.location.href
      });
    }
  };

  // Cookie consent state
  const [showCookieBanner, setShowCookieBanner] = useState(false);
  const [cookieConsent, setCookieConsent] = useState(null);

  // Check cookie consent on load
  useEffect(() => {
    const consent = localStorage.getItem('cookie_consent');
    if (consent) {
      setCookieConsent(consent);
    } else {
      setShowCookieBanner(true);
    }
  }, []);

  // Cookie consent handlers
  const acceptCookies = () => {
    localStorage.setItem('cookie_consent', 'accepted');
    setCookieConsent('accepted');
    setShowCookieBanner(false);
  };

  const rejectCookies = () => {
    localStorage.setItem('cookie_consent', 'rejected');
    setCookieConsent('rejected');
    setShowCookieBanner(false);
    // Clear any existing cookies except essential ones
    localStorage.removeItem('offline_favorites');
  };

  const acceptNecessaryOnly = () => {
    localStorage.setItem('cookie_consent', 'necessary');
    setCookieConsent('necessary');
    setShowCookieBanner(false);
  };

  // Save theme preference
  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // Save favorites offline
  useEffect(() => {
    localStorage.setItem('offline_favorites', JSON.stringify(favorites));
  }, [favorites]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [stationsRes, countriesRes, languagesRes, tagsRes, favoritesRes] = await Promise.all([
        axios.get(`${API}/stations?limit=200&order=clickcount&reverse=true`),
        axios.get(`${API}/countries`),
        axios.get(`${API}/languages`),
        axios.get(`${API}/tags`),
        axios.get(`${API}/favorites/${USER_ID}`).catch(() => ({data: []}))
      ]);

      setStations(stationsRes.data.stations);
      setTotalStations(stationsRes.data.stations.length);
      setHasMoreStations(stationsRes.data.stations.length === 200); // Enable load more
      
      // Show all countries, sorted by station count
      const sortedCountries = countriesRes.data.countries
        .filter(country => country.stationcount > 0)
        .sort((a, b) => b.stationcount - a.stationcount);
      setCountries(sortedCountries);
      
      // Show more languages, sorted by station count  
      const sortedLanguages = languagesRes.data.languages
        .filter(lang => lang.stationcount > 0)
        .sort((a, b) => b.stationcount - a.stationcount);
      setLanguages(sortedLanguages.slice(0, 50));
      
      // Show top tags
      const sortedTags = tagsRes.data.tags
        .filter(tag => tag.stationcount > 0)
        .sort((a, b) => b.stationcount - a.stationcount);
      setTags(sortedTags.slice(0, 100));
      
      // Merge online and offline favorites
      const onlineFavorites = favoritesRes.data || [];
      const offlineFavorites = JSON.parse(localStorage.getItem('offline_favorites') || '[]');
      const mergedFavorites = [...onlineFavorites, ...offlineFavorites.filter(
        offline => !onlineFavorites.some(online => online.station_uuid === offline.station_uuid)
      )];
      setFavorites(mergedFavorites);
      
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchStations = async (loadMore = false) => {
    try {
      console.log('🔍 Search started:', { searchQuery, selectedCountry, selectedLanguage, selectedTag, loadMore });
      
      // Track search/filter actions
      if (!loadMore) {
        if (searchQuery && searchQuery.trim().length > 0) {
          trackUserInteraction('search', { 
            search_query: searchQuery,
            search_type: 'text_search'
          });
        } else if (selectedCountry || selectedLanguage || selectedTag) {
          trackUserInteraction('filter_stations', { 
            country: selectedCountry,
            language: selectedLanguage,
            tag: selectedTag,
            filter_type: selectedCountry ? 'country' : selectedLanguage ? 'language' : 'tag'
          });
        }
      }
      
      if (!loadMore) {
        setLoading(true);
        setCurrentPage(0);
        setStations([]); // Clear current stations
      } else {
        setLoadingMore(true);
        trackUserInteraction('load_more', { 
          current_stations_count: stations.length,
          country: selectedCountry,
          search_query: searchQuery
        });
      }

      const page = loadMore ? currentPage + 1 : 0;
      const limit = 200;
      const offset = page * limit;

      let url = `${API}/stations?limit=${limit}&offset=${offset}&order=${sortBy}&reverse=true`;
      
      // FIXED: Check if searchQuery has actual content, not just existence
      if (searchQuery && searchQuery.trim().length > 0) {
        url = `${API}/search?q=${encodeURIComponent(searchQuery.trim())}&limit=${limit}&offset=${offset}`;
        console.log('🔍 Using search endpoint:', url);
      } else {
        // Apply filters to stations endpoint
        if (selectedCountry) url += `&country=${encodeURIComponent(selectedCountry)}`;
        if (selectedLanguage) url += `&language=${encodeURIComponent(selectedLanguage)}`;
        if (selectedTag) url += `&tag=${encodeURIComponent(selectedTag)}`;
        console.log('🔍 Using filter endpoint:', url);
      }

      const response = await axios.get(url);
      console.log('📡 Raw API Response:', response.data);
      
      let filteredStations = response.data.stations || [];
      console.log('📊 Stations received:', filteredStations.length);

      // Apply advanced filters
      let advancedFiltered = filteredStations;
      if (bitrateMin) {
        advancedFiltered = advancedFiltered.filter(s => s.bitrate >= parseInt(bitrateMin));
      }
      if (bitrateMax) {
        advancedFiltered = advancedFiltered.filter(s => s.bitrate <= parseInt(bitrateMax));
      }
      if (selectedCodec) {
        advancedFiltered = advancedFiltered.filter(s => 
          s.codec && s.codec.toLowerCase().includes(selectedCodec.toLowerCase())
        );
      }

      console.log('🔧 After advanced filters:', advancedFiltered.length);

      // Update stations state
      if (loadMore) {
        setStations(prevStations => {
          const newStations = [...prevStations, ...advancedFiltered];
          console.log('➕ Load more - Total stations now:', newStations.length);
          return newStations;
        });
      } else {
        console.log('🔄 Setting new stations:', advancedFiltered.length);
        setStations(advancedFiltered);
        
        // Track search results
        trackUserInteraction('search_results', { 
          results_count: advancedFiltered.length,
          search_query: searchQuery,
          country: selectedCountry,
          language: selectedLanguage,
          tag: selectedTag
        });
        
        // Force a re-render by updating totalStations
        setTotalStations(advancedFiltered.length);
      }

      setCurrentPage(page);
      setHasMoreStations(advancedFiltered.length === limit);

    } catch (error) {
      console.error('❌ Search error:', error);
      trackUserInteraction('search_error', { 
        error: error.message,
        search_query: searchQuery,
        country: selectedCountry
      });
      // On error, don't clear stations, just stop loading
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const loadMoreStations = () => {
    if (!loadingMore && hasMoreStations) {
      searchStations(true);
    }
  };

  const playStation = async (station) => {
    try {
      if (currentStation?.stationuuid === station.stationuuid && isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
        trackUserInteraction('pause_station', { 
          station_name: station.name, 
          station_country: station.country,
          station_uuid: station.stationuuid 
        });
        return;
      }

      setCurrentStation(station);
      if (audioRef.current) {
        audioRef.current.src = station.url_resolved || station.url;
        audioRef.current.volume = volume;
        await audioRef.current.play();
        setIsPlaying(true);
        
        trackUserInteraction('play_station', { 
          station_name: station.name, 
          station_country: station.country,
          station_language: station.language,
          station_genre: station.tags,
          station_uuid: station.stationuuid 
        });
        
        // Register click with Radio Browser API for popularity metrics
        await axios.post(`${API}/stations/${station.stationuuid}/click`).catch(() => {});
      }
    } catch (error) {
      console.error('Error playing station:', error);
      setIsPlaying(false);
      trackUserInteraction('play_error', { 
        station_name: station.name, 
        error: error.message,
        station_uuid: station.stationuuid 
      });
    }
  };

  const toggleFavorite = async (station) => {
    try {
      const isFavorite = favorites.some(fav => fav.station_uuid === station.stationuuid);
      
      if (isFavorite) {
        // Remove from online and offline
        await axios.delete(`${API}/favorites/${USER_ID}/${station.stationuuid}`).catch(() => {});
        setFavorites(favorites.filter(fav => fav.station_uuid !== station.stationuuid));
        
        trackUserInteraction('remove_favorite', { 
          station_name: station.name, 
          station_country: station.country,
          station_uuid: station.stationuuid 
        });
      } else {
        // Add to both online and offline
        const favoriteData = {
          user_id: USER_ID,
          station_uuid: station.stationuuid,
          station_name: station.name,
          station_country: station.country,
          station_url: station.url_resolved || station.url
        };
        
        try {
          const response = await axios.post(`${API}/favorites`, favoriteData);
          setFavorites([...favorites, response.data]);
          
          trackUserInteraction('add_favorite', { 
            station_name: station.name, 
            station_country: station.country,
            station_genre: station.tags,
            station_uuid: station.stationuuid 
          });
        } catch (error) {
          // Fallback to offline only
          const offlineFavorite = {
            ...favoriteData,
            id: 'offline_' + Date.now(),
            created_at: new Date().toISOString()
          };
          setFavorites([...favorites, offlineFavorite]);
          
          trackUserInteraction('add_favorite', { 
            station_name: station.name, 
            station_country: station.country,
            station_genre: station.tags,
            station_uuid: station.stationuuid 
          });
        }
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      trackUserInteraction('favorite_error', { 
        station_name: station.name, 
        error: error.message,
        station_uuid: station.stationuuid 
      });
    }
  };

  const isFavorite = (stationUuid) => {
    return favorites.some(fav => fav.station_uuid === stationUuid);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
  };

  // Share functionality
  const openShareModal = (station) => {
    setShareStation(station);
    setShowShareModal(true);
  };

  const closeShareModal = () => {
    setShowShareModal(false);
    setShareStation(null);
  };

  const shareToFacebook = (station) => {
    const url = encodeURIComponent(window.location.href);
    const title = encodeURIComponent(`🎵 Ακούω ${station.name} από ${station.country} - AIPANTHEONGR-RADIO`);
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${title}`;
    
    trackUserInteraction('share', { 
      platform: 'facebook',
      station_name: station.name,
      station_country: station.country,
      station_uuid: station.stationuuid
    });
    
    // Try to open in new window, fallback to same window if blocked
    const newWindow = window.open(facebookUrl, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
    if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
      // Popup blocked, open in same window
      window.location.href = facebookUrl;
    }
  };

  const shareToTwitter = (station) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`🎵 Ακούω ${station.name} από ${station.country} στο AIPANTHEONGR-RADIO! 📻 #Radio #Music`);
    const twitterUrl = `https://twitter.com/intent/tweet?text=${text}&url=${url}`;
    
    trackUserInteraction('share', { 
      platform: 'twitter',
      station_name: station.name,
      station_country: station.country,
      station_uuid: station.stationuuid
    });
    
    const newWindow = window.open(twitterUrl, '_blank', 'width=600,height=400,scrollbars=yes,resizable=yes');
    if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
      window.location.href = twitterUrl;
    }
  };

  const shareToWhatsApp = (station) => {
    const text = encodeURIComponent(`🎵 Ακούω ${station.name} από ${station.country} στο AIPANTHEONGR-RADIO! 📻 Δες και εσύ: ${window.location.href}`);
    const whatsappUrl = `https://wa.me/?text=${text}`;
    
    trackUserInteraction('share', { 
      platform: 'whatsapp',
      station_name: station.name,
      station_country: station.country,
      station_uuid: station.stationuuid
    });
    
    const newWindow = window.open(whatsappUrl, '_blank');
    if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
      window.location.href = whatsappUrl;
    }
  };

  const shareToTelegram = (station) => {
    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`🎵 Ακούω ${station.name} από ${station.country} - AIPANTHEONGR-RADIO! 📻`);
    const telegramUrl = `https://t.me/share/url?url=${url}&text=${text}`;
    
    trackUserInteraction('share', { 
      platform: 'telegram',
      station_name: station.name,
      station_country: station.country,
      station_uuid: station.stationuuid
    });
    
    const newWindow = window.open(telegramUrl, '_blank');
    if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
      window.location.href = telegramUrl;
    }
  };

  const shareViaEmail = (station) => {
    const subject = encodeURIComponent(`Ραδιοφωνικός Σταθμός: ${station.name}`);
    const body = encodeURIComponent(`Γεια σου!\n\nΘα ήθελα να μοιραστώ μαζί σου αυτόν τον υπέροχο ραδιοφωνικό σταθμό:\n\n🎵 ${station.name}\n🌍 ${station.country}\n🗣️ ${station.language}\n\nΜπορείς να τον ακούσεις εδώ: ${window.location.href}\n\nΚαλή ακρόαση!`);
    const mailtoUrl = `mailto:?subject=${subject}&body=${body}`;
    
    trackUserInteraction('share', { 
      platform: 'email',
      station_name: station.name,
      station_country: station.country,
      station_uuid: station.stationuuid
    });
    
    window.location.href = mailtoUrl;
  };

  const shareViaSMS = (station) => {
    const text = encodeURIComponent(`🎵 Ακούω ${station.name} από ${station.country}! Δες: ${window.location.href}`);
    const smsUrl = `sms:?body=${text}`;
    
    trackUserInteraction('share', { 
      platform: 'sms',
      station_name: station.name,
      station_country: station.country,
      station_uuid: station.stationuuid
    });
    
    window.location.href = smsUrl;
  };

  const copyToClipboard = async (station) => {
    const shareText = `🎵 ${station.name} από ${station.country} - ${window.location.href}`;
    
    trackUserInteraction('share', { 
      platform: 'clipboard',
      station_name: station.name,
      station_country: station.country,
      station_uuid: station.stationuuid
    });
    
    try {
      await navigator.clipboard.writeText(shareText);
      alert('Η διεύθυνση αντιγράφηκε στο πρόχειρο!');
    } catch (err) {
      // Fallback για παλιά browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      alert('Η διεύθυνση αντιγράφηκε στο πρόχειρο!');
    }
  };

  const shareToInstagram = (station) => {
    trackUserInteraction('share', { 
      platform: 'instagram',
      station_name: station.name,
      station_country: station.country,
      station_uuid: station.stationuuid
    });
    
    // Instagram δεν υποστηρίζει direct sharing, οπότε αντιγράφουμε το link
    copyToClipboard(station);
    alert('Η διεύθυνση αντιγράφηκε! Πάστε τη στο Instagram story ή post σας.');
  };

  const shareToTikTok = (station) => {
    trackUserInteraction('share', { 
      platform: 'tiktok',
      station_name: station.name,
      station_country: station.country,
      station_uuid: station.stationuuid
    });
    
    // TikTok δεν υποστηρίζει direct sharing, οπότε αντιγράφουμε το link  
    copyToClipboard(station);
    alert('Η διεύθυνση αντιγράφηκε! Πάστε τη στο TikTok video σας.');
  };

  // Privacy Policy component
  const PrivacyPolicyContent = () => (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h1 className="text-3xl font-bold mb-6">Πολιτική Απορρήτου</h1>
      <p className="text-sm text-gray-400 mb-6">Τελευταία ενημέρωση: {new Date().toLocaleDateString('el-GR')}</p>
      
      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">1. Εισαγωγή</h2>
        <p>
          Το Παγκόσμιο Ράδιο (που λειτουργείται από AI Pantheon GR) σέβεται την ιδιωτικότητά σας και δεσμεύεται να προστατεύει τα προσωπικά σας δεδομένα. 
          Αυτή η πολιτική απορρήτου εξηγεί πώς συλλέγουμε, χρησιμοποιούμε και προστατεύουμε τις πληροφορίες σας όταν χρησιμοποιείτε την ιστοσελίδα μας.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">2. Δεδομένα που Συλλέγουμε</h2>
        <h3 className="text-xl font-medium">2.1 Δεδομένα που παρέχετε εσείς:</h3>
        <ul className="list-disc pl-6 space-y-2">
          <li>Αγαπημένοι ραδιοφωνικοί σταθμοί (αποθηκεύονται τοπικά στο browser σας)</li>
          <li>Προτιμήσεις αναζήτησης και φίλτρων</li>
          <li>Στοιχεία που μοιράζεστε εθελοντικά μέσω social media</li>
        </ul>
        
        <h3 className="text-xl font-medium">2.2 Δεδομένα που συλλέγονται αυτόματα:</h3>
        <ul className="list-disc pl-6 space-y-2">
          <li>Διεύθυνση IP και τοποθεσία (για τοπικές συστάσεις σταθμών)</li>
          <li>Τύπος browser και λειτουργικό σύστημα</li>
          <li>Στατιστικά χρήσης και προτιμήσεις ακρόασης</li>
          <li>Cookies για τη λειτουργικότητα της ιστοσελίδας</li>
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">3. Πώς Χρησιμοποιούμε τα Δεδομένα σας</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Για την παροχή και βελτίωση των υπηρεσιών μας</li>
          <li>Για την εξατομίκευση της εμπειρίας σας</li>
          <li>Για την παροχή τοπικών συστάσεων ραδιοφωνικών σταθμών</li>
          <li>Για την ανάλυση της χρήσης και τη βελτίωση της ιστοσελίδας</li>
          <li>Για την επικοινωνία μαζί σας σχετικά με ενημερώσεις</li>
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">4. Νομική Βάση Επεξεργασίας (GDPR)</h2>
        <p>Επεξεργαζόμαστε τα προσωπικά σας δεδομένα βάσει:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Συναίνεσης:</strong> Όταν δίνετε σαφή συναίνεση για συγκεκριμένες επεξεργασίες</li>
          <li><strong>Νόμιμου Συμφέροντος:</strong> Για τη βελτίωση των υπηρεσιών και την ασφάλεια</li>
          <li><strong>Εκτέλεσης Συμβολαίου:</strong> Για την παροχή των υπηρεσιών που ζητάτε</li>
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">5. Κοινοποίηση Δεδομένων</h2>
        <p>Δεν πουλάμε, ενοικιάζουμε ή μοιραζόμαστε τα προσωπικά σας δεδομένα με τρίτους, εκτός από:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Παρόχους Υπηρεσιών:</strong> Radio Browser API για δεδομένα σταθμών</li>
          <li><strong>Κοινωνικά Δίκτυα:</strong> Όταν επιλέγετε να μοιραστείτε περιεχόμενο</li>
          <li><strong>Νομικές Υποχρεώσεις:</strong> Όταν απαιτείται από το νόμο</li>
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">6. Cookies και Παρόμοιες Τεχνολογίες</h2>
        <p>Χρησιμοποιούμε cookies για:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Απαραίτητα Cookies:</strong> Για τη βασική λειτουργία της ιστοσελίδας</li>
          <li><strong>Προτιμήσεις:</strong> Για την αποθήκευση των ρυθμίσεών σας (θέμα, γλώσσα)</li>
          <li><strong>Αγαπημένα:</strong> Για την αποθήκευση των αγαπημένων σας σταθμών</li>
        </ul>
        <p className="text-sm text-gray-300">
          Μπορείτε να διαχειριστείτε τις προτιμήσεις cookies στις ρυθμίσεις του browser σας.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">7. Τα Δικαιώματά σας (GDPR)</h2>
        <p>Έχετε τα ακόλουθα δικαιώματα:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Πρόσβαση:</strong> Να ζητήσετε πληροφορίες για τα δεδομένα που κατέχουμε</li>
          <li><strong>Διόρθωση:</strong> Να ζητήσετε διόρθωση ανακριβών δεδομένων</li>
          <li><strong>Διαγραφή:</strong> Να ζητήσετε διαγραφή των δεδομένων σας</li>
          <li><strong>Περιορισμός:</strong> Να περιορίσετε την επεξεργασία των δεδομένων σας</li>
          <li><strong>Φορητότητα:</strong> Να λάβετε τα δεδομένα σας σε δομημένη μορφή</li>
          <li><strong>Αντίρρηση:</strong> Να αντιταχθείτε στην επεξεργασία των δεδομένων σας</li>
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">8. Ασφάλεια Δεδομένων</h2>
        <p>Λαμβάνουμε κατάλληλα τεχνικά και οργανωτικά μέτρα για να προστατεύσουμε τα δεδομένα σας:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Κρυπτογράφηση SSL/TLS για όλες τις επικοινωνίες</li>
          <li>Ασφαλής αποθήκευση δεδομένων</li>
          <li>Περιορισμένη πρόσβαση στα δεδομένα</li>
          <li>Τακτικές ενημερώσεις ασφαλείας</li>
        </ul>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">9. Παιδιά και Ανήλικοι</h2>
        <p>
          Οι υπηρεσίες μας δεν απευθύνονται σε παιδιά κάτω των 16 ετών. Δεν συλλέγουμε συνειδητά 
          προσωπικές πληροφορίες από παιδιά κάτω των 16 ετών χωρίς τη συγκατάθεση των γονέων.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">10. Διεθνείς Μεταφορές</h2>
        <p>
          Τα δεδομένα σας μπορεί να επεξεργάζονται σε χώρες εκτός της ΕΕ. Διασφαλίζουμε ότι τέτοιες 
          μεταφορές γίνονται σύμφωνα με το GDPR και με κατάλληλες εγγυήσεις ασφαλείας.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">11. Αλλαγές στην Πολιτική Απορρήτου</h2>
        <p>
          Μπορούμε να ενημερώνουμε αυτή την πολιτική απορρήτου περιστασιακά. Θα σας ειδοποιήσουμε 
          για σημαντικές αλλαγές τοποθετώντας μια εμφανή ανακοίνωση στην ιστοσελίδα μας.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">12. Επικοινωνία</h2>
        <p>Για ερωτήσεις σχετικά με αυτή την πολιτική απορρήτου ή τα δικαιώματά σας, επικοινωνήστε μαζί μας:</p>
        <div className="bg-gray-700 p-4 rounded-lg">
          <p><strong>AI Agency M.IKE</strong></p>
          <p>Αρχαιολογικό Μουσείο 17</p>
          <p>54640 Θεσσαλονίκη</p>
          <p>Email: info@ai-agency.gr</p>
          <p>Instagram: @aipantheongr</p>
          <p>Υπεύθυνος Προστασίας Δεδομένων: info@ai-agency.gr</p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">13. Αποποίηση Ευθύνης για Περιεχόμενο Ραδιοσταθμών</h2>
        <div className="bg-red-900 bg-opacity-20 border border-red-500 p-4 rounded-lg">
          <p className="font-semibold text-red-400 mb-2">ΣΗΜΑΝΤΙΚΗ ΔΗΛΩΣΗ:</p>
          <p>
            <strong>Η AI Agency M.IKE και το Παγκόσμιο Ράδιο λειτουργούν αποκλειστικά ως πλατφόρμα συγκέντρωσης και παρουσίασης ραδιοφωνικών σταθμών.</strong>
          </p>
          <ul className="list-disc pl-6 space-y-2 mt-4">
            <li><strong>ΔΕΝ παράγουμε, ελέγχουμε ή επιμελούμαστε το περιεχόμενο</strong> των ραδιοφωνικών σταθμών</li>
            <li><strong>ΔΕΝ φέρουμε καμία ευθύνη</strong> για το περιεχόμενο, τη μουσική, τις απόψεις ή τις δηλώσεις των σταθμών</li>
            <li><strong>Οι ραδιοσταθμοί είναι ανεξάρτητες οντότητες</strong> με δικές τους άδειες και ευθύνες</li>
            <li><strong>Χρησιμοποιούμε το Radio-Browser API</strong> - μια δημόσια, ανοιχτή βάση δεδομένων</li>
            <li><strong>Κάθε σταθμός είναι υπεύθυνος</strong> για τη νομιμότητα του περιεχομένου του</li>
            <li><strong>Οι χρήστες ακούν με δική τους ευθύνη</strong> και διακριτική ευχέρεια</li>
          </ul>
          <p className="mt-4 text-sm">
            Σε περίπτωση παραπόνων για συγκεκριμένο περιεχόμενο, παρακαλούμε επικοινωνήστε απευθείας με τον αντίστοιχο ραδιοσταθμό.
          </p>
        </div>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">14. Πνευματικά Δικαιώματα και Άδειες</h2>
        <p>
          Όλα τα πνευματικά δικαιώματα για τη μουσική και το περιεχόμενο που αναπαράγεται ανήκουν στους αντίστοιχους 
          ραδιοσταθμούς και τους κατόχους δικαιωμάτων. Η πλατφόρμα μας δεν κατέχει ούτε διεκδικεί κανένα δικαίωμα 
          επί του περιεχομένου των σταθμών.
        </p>
      </section>

      <section className="space-y-4">
        <h2 className="text-2xl font-semibold">15. Αρχή Προστασίας Δεδομένων</h2>
        <p>
          Έχετε το δικαίωμα να υποβάλετε καταγγελία στην αρμόδια Αρχή Προστασίας Δεδομένων εάν 
          θεωρείτε ότι η επεξεργασία των προσωπικών σας δεδομένων παραβιάζει το GDPR.
        </p>
        <div className="bg-gray-700 p-4 rounded-lg">
          <p><strong>Αρχή Προστασίας Δεδομένων Προσωπικού Χαρακτήρα</strong></p>
          <p>Ιστοσελίδα: www.dpa.gr</p>
          <p>Email: contact@dpa.gr</p>
        </div>
      </section>
    </div>
  );

  const displayStations = showFavorites 
    ? stations.filter(station => isFavorite(station.stationuuid))
    : stations;

  const themeClasses = theme === 'light' 
    ? 'bg-gray-50 text-gray-900' 
    : 'bg-gray-900 text-white';

  return (
    <div className={`min-h-screen transition-all duration-300 ${themeClasses}`}>
      {!showPrivacyPolicy ? (
        <>
          {/* Logo and Branding removed per user request */}

          {/* Theme Controls */}
          <div className="fixed mobile-safe-top right-4 z-50">
            <button
              onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
              className="px-3 py-1 rounded bg-gray-800 hover:bg-gray-700 text-white transition-colors"
            >
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
          </div>

      {/* Custom Banner Hero Section */}
      <div 
        className="relative pt-24 pb-16 px-4 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.6)), url('https://customer-assets.emergentagent.com/job_tuneglobe-23/artifacts/yvmxakz6_the_puma_group_58698_imagine_a_realistic_high-resolution_phot_ed288411-0606-4b5f-a808-a72ea84bfefb_2.png')`,
          minHeight: '600px'
        }}
      >
        <div className="text-center max-w-6xl mx-auto">
          {/* Main Title with Logo */}
          <div className="flex items-center justify-center mb-6 space-x-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_tuneglobe-23/artifacts/6g9pjdot_Untitled%20design%20%2815%29.png"
              alt="AIPANTHEONGR Logo"
              className="w-16 h-16 md:w-20 md:h-20 object-contain"
            />
            <h1 className="text-5xl md:text-7xl font-bold text-white drop-shadow-lg">
              AIPANTHEONGR-RADIO
            </h1>
          </div>
          <p className="text-xl md:text-2xl text-white mb-12 max-w-3xl mx-auto drop-shadow-md">
            Ανακαλύψτε πάνω από 53.000 ραδιοφωνικούς σταθμούς από όλο τον κόσμο
          </p>
          
          {/* Modern Search Bar */}
          <div className="max-w-3xl mx-auto mb-8">
            <div className="relative">
              <input
                type="text"
                placeholder="Αναζητήστε ραδιοφωνικούς σταθμούς..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-6 py-4 pr-14 text-lg rounded-2xl border-2 focus:outline-none transition-all duration-300 bg-white/90 backdrop-blur-sm border-white/50 text-gray-900 focus:border-blue-500 shadow-lg placeholder-gray-600"
                onKeyPress={(e) => e.key === 'Enter' && searchStations()}
              />
              <button
                onClick={searchStations}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-10 h-10 bg-blue-600 hover:bg-blue-700 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-105 shadow-lg"
              >
                <span className="text-white text-lg">🔍</span>
              </button>
            </div>
          </div>

          {/* Modern Genre Pills */}
          <div className="flex flex-wrap justify-center gap-3 mb-8 max-w-4xl mx-auto">
            {POPULAR_GENRES.slice(0, 8).map(genre => (
              <button
                key={genre}
                onClick={() => {
                  setSelectedTag(genre);
                  setSelectedGenre(genre);
                  searchStations();
                }}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 transform hover:scale-105 backdrop-blur-sm border shadow-lg ${
                  selectedGenre === genre
                    ? 'bg-blue-600 text-white border-blue-500'
                    : 'bg-white/20 text-white hover:bg-blue-600 hover:text-white border-white/30'
                }`}
              >
                {genre}
              </button>
            ))}
          </div>

          {/* Advanced Search Toggle */}
          <button
            onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
            className="text-sm font-medium transition-colors duration-300 flex items-center mx-auto space-x-2 text-white/90 hover:text-white backdrop-blur-sm bg-black/20 px-4 py-2 rounded-full border border-white/30"
          >
            <span>{showAdvancedSearch ? '▲' : '▼'}</span>
            <span>{showAdvancedSearch ? 'Απόκρυψη προηγμένης αναζήτησης' : 'Εμφάνιση προηγμένης αναζήτησης'}</span>
          </button>

          {/* Social Media Links */}
          <div className="flex justify-center space-x-6 mt-12">
            <a
              href="https://www.instagram.com/aipantheongr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-14 h-14 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-full transition-all duration-300 transform hover:scale-110 shadow-lg backdrop-blur-sm"
              title="Follow us on Instagram"
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
            </a>
            <a
              href="https://www.tiktok.com/@aipantheongr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-14 h-14 bg-black hover:bg-gray-800 rounded-full transition-all duration-300 transform hover:scale-110 shadow-lg backdrop-blur-sm"
              title="Follow us on TikTok"
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-.88-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
            </a>
            <a
              href="https://www.youtube.com/@aipantheongr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-14 h-14 bg-red-600 hover:bg-red-700 rounded-full transition-all duration-300 transform hover:scale-110 shadow-lg backdrop-blur-sm"
              title="Subscribe on YouTube"
            >
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="white"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </a>
          </div>
        </div>
      </div>

      {/* Modern Advanced Search Panel */}
      {showAdvancedSearch && (
        <div className={`mx-4 mb-8 p-6 rounded-2xl border-2 max-w-6xl mx-auto transition-all duration-300 ${
          theme === 'light' 
            ? 'bg-white border-gray-200 shadow-sm' 
            : 'bg-gray-800 border-gray-600 shadow-lg'
        }`}>
          <h3 className="text-lg font-semibold mb-4 text-center">Προηγμένες Επιλογές</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Ελάχιστο Bitrate (kbps)</label>
              <input
                type="number"
                value={bitrateMin}
                onChange={(e) => setBitrateMin(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-all duration-300 ${
                  theme === 'light' 
                    ? 'bg-white border-gray-200 text-gray-900 focus:border-blue-500' 
                    : 'bg-gray-700 border-gray-600 text-white focus:border-blue-400'
                }`}
                placeholder="π.χ. 128"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Μέγιστο Bitrate (kbps)</label>
              <input
                type="number"
                value={bitrateMax}
                onChange={(e) => setBitrateMax(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-all duration-300 ${
                  theme === 'light' 
                    ? 'bg-white border-gray-200 text-gray-900 focus:border-blue-500' 
                    : 'bg-gray-700 border-gray-600 text-white focus:border-blue-400'
                }`}
                placeholder="π.χ. 320"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Codec</label>
              <select
                value={selectedCodec}
                onChange={(e) => setSelectedCodec(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-all duration-300 ${
                  theme === 'light' 
                    ? 'bg-white border-gray-200 text-gray-900 focus:border-blue-500' 
                    : 'bg-gray-700 border-gray-600 text-white focus:border-blue-400'
                }`}
              >
                <option value="">Όλα τα Codecs</option>
                <option value="MP3">MP3</option>
                <option value="AAC">AAC</option>
                <option value="OGG">OGG</option>
                <option value="FLAC">FLAC</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Ταξινόμηση κατά</label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className={`w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-all duration-300 ${
                  theme === 'light' 
                    ? 'bg-white border-gray-200 text-gray-900 focus:border-blue-500' 
                    : 'bg-gray-700 border-gray-600 text-white focus:border-blue-400'
                }`}
              >
                <option value="clickcount">Δημοτικότητα</option>
                <option value="name">Όνομα</option>
                <option value="bitrate">Bitrate</option>
                <option value="votes">Αξιολογήσεις</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Modern Current Playing Station */}
      {currentStation && (
        <div className={`border-b p-6 ${
          theme === 'light' 
            ? 'bg-white border-gray-200 shadow-sm' 
            : 'bg-gray-800 border-gray-600 shadow-lg'
        }`}>
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <img 
                    src={currentStation.favicon || '/api/placeholder/64/64'} 
                    alt=""
                    className="w-16 h-16 rounded-xl bg-gray-700 border-2 border-blue-500"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <span className="text-white text-xs">🎵</span>
                  </div>
                </div>
                <div>
                  <h3 className="font-bold text-lg">{currentStation.name}</h3>
                  <p className="text-sm opacity-70">
                    🌍 {currentStation.country} • 🗣️ {currentStation.language}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => playStation(currentStation)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${
                    isPlaying 
                      ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg' 
                      : 'bg-green-600 hover:bg-green-700 text-white shadow-lg'
                  }`}
                >
                  {isPlaying ? '⏸️ Παύση' : '▶️ Αναπαραγωγή'}
                </button>
                
                <div className="flex items-center space-x-3 bg-gray-100 dark:bg-gray-700 px-4 py-2 rounded-xl">
                  <span className="text-lg">🔊</span>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-24 accent-blue-500"
                  />
                  <span className="text-sm font-medium">{Math.round(volume * 100)}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Modern Filters Sidebar */}
          <div className="lg:w-1/4">
            <div className={`rounded-2xl p-6 sticky top-4 border-2 ${
              theme === 'light' 
                ? 'bg-white border-gray-200 shadow-sm' 
                : 'bg-gray-800 border-gray-600 shadow-lg'
            }`}>
              <h2 className="text-xl font-bold mb-6">Φίλτρα</h2>
              
              <div className="space-y-6">
                <div>
                  <button
                    onClick={() => setShowFavorites(!showFavorites)}
                    className={`w-full px-4 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${
                      showFavorites 
                        ? 'bg-yellow-500 text-white shadow-lg' 
                        : theme === 'light'
                          ? 'bg-gray-100 text-gray-700 hover:bg-yellow-500 hover:text-white'
                          : 'bg-gray-700 text-gray-300 hover:bg-yellow-500 hover:text-white'
                    }`}
                  >
                    ⭐ {showFavorites ? 'Εμφάνιση όλων' : 'Εμφάνιση αγαπημένων'} ({favorites.length})
                  </button>
                  
                  {favorites.length > 0 && !showFavorites && (
                    <div className="mt-2 text-xs text-gray-400">
                      💡 Έχετε {favorites.length} αγαπημένους σταθμούς αποθηκευμένους
                    </div>
                  )}
                  
                  {showFavorites && favorites.length === 0 && (
                    <div className="mt-2 text-xs text-gray-400">
                      Δεν έχετε αγαπημένους σταθμούς ακόμα. Κάντε κλικ στο ⭐ για να προσθέσετε!
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-3">Χώρα</label>
                  <select
                    value={selectedCountry}
                    onChange={(e) => setSelectedCountry(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-all duration-300 ${
                      theme === 'light' 
                        ? 'bg-white border-gray-200 text-gray-900 focus:border-blue-500' 
                        : 'bg-gray-700 border-gray-600 text-white focus:border-blue-400'
                    }`}
                  >
                    <option value="">Όλες οι Χώρες</option>
                    {countries.map(country => (
                      <option key={country.name} value={country.name}>
                        {country.name} ({country.stationcount})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-3">Γλώσσα</label>
                  <select
                    value={selectedLanguage}
                    onChange={(e) => setSelectedLanguage(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-all duration-300 ${
                      theme === 'light' 
                        ? 'bg-white border-gray-200 text-gray-900 focus:border-blue-500' 
                        : 'bg-gray-700 border-gray-600 text-white focus:border-blue-400'
                    }`}
                  >
                    <option value="">Όλες οι Γλώσσες</option>
                    {languages.map(language => (
                      <option key={language.name} value={language.name}>
                        {language.name} ({language.stationcount})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold mb-3">Είδος/Ετικέτα</label>
                  <select
                    value={selectedTag}
                    onChange={(e) => setSelectedTag(e.target.value)}
                    className={`w-full px-4 py-3 rounded-xl border-2 focus:outline-none transition-all duration-300 ${
                      theme === 'light' 
                        ? 'bg-white border-gray-200 text-gray-900 focus:border-blue-500' 
                        : 'bg-gray-700 border-gray-600 text-white focus:border-blue-400'
                    }`}
                  >
                    <option value="">Όλα τα Είδη</option>
                    {tags.map(tag => (
                      <option key={tag.name} value={tag.name}>
                        {tag.name} ({tag.stationcount})
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-3">
                  <button
                    onClick={() => {
                      // Clear search query when applying filters
                      setSearchQuery('');
                      searchStations();
                    }}
                    className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Εφαρμογή Φίλτρων
                  </button>

                  <button
                    onClick={() => {
                      setSelectedCountry('');
                      setSelectedLanguage('');
                      setSelectedTag('');
                      setSearchQuery('');
                      setShowFavorites(false);
                      setBitrateMin('');
                      setBitrateMax('');
                      setSelectedCodec('');
                      setSelectedGenre('');
                      loadInitialData();
                    }}
                    className={`w-full px-4 py-3 rounded-xl font-medium transition-all duration-300 transform hover:scale-105 ${
                      theme === 'light'
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Επαναφορά Όλων
                  </button>
                </div>

                {/* View Mode Toggle */}
                <div className="pt-4 border-t border-gray-300 dark:border-gray-600">
                  <label className="block text-sm font-semibold mb-3">Προβολή</label>
                  <div className="flex rounded-xl overflow-hidden border-2 border-gray-200 dark:border-gray-600">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`flex-1 px-4 py-3 text-sm font-medium transition-all duration-300 ${
                        viewMode === 'grid' 
                          ? 'bg-blue-600 text-white' 
                          : theme === 'light'
                            ? 'bg-white text-gray-700 hover:bg-blue-600 hover:text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-blue-600 hover:text-white'
                      }`}
                    >
                      📱 Κάρτες
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`flex-1 px-4 py-3 text-sm font-medium transition-all duration-300 ${
                        viewMode === 'list' 
                          ? 'bg-blue-600 text-white' 
                          : theme === 'light'
                            ? 'bg-white text-gray-700 hover:bg-blue-600 hover:text-white'
                            : 'bg-gray-700 text-gray-300 hover:bg-blue-600 hover:text-white'
                      }`}
                    >
                      📋 Λίστα
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Stations Display */}
          <div className="lg:w-3/4">
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
                <p className="mt-4 opacity-70">Φόρτωση ραδιοφωνικών σταθμών...</p>
              </div>
            ) : (
              <>
                {/* Results Header */}
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-semibold">
                    {showFavorites 
                      ? `${displayStations.length} αγαπημένοι σταθμοί`
                      : `${stations.length} σταθμοί βρέθηκαν`
                    }
                    {!showFavorites && totalStations > stations.length && ` (από ${totalStations}+ διαθέσιμους)`}
                  </h3>
                </div>

                {/* Modern Stations Grid/List */}
                <div className={viewMode === 'grid' 
                  ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                  : "space-y-4"
                }>
                  {displayStations.map(station => (
                    <div 
                      key={station.stationuuid} 
                      className={`rounded-2xl transition-all duration-300 hover:shadow-lg border-2 ${
                        theme === 'light'
                          ? 'bg-white border-gray-200 hover:border-blue-300 shadow-sm hover:shadow-md'
                          : 'bg-gray-800 border-gray-600 hover:border-blue-500 shadow-lg hover:shadow-xl'
                      } ${
                        viewMode === 'list' 
                          ? 'flex items-center space-x-4 p-4' 
                          : 'p-6 h-72 flex flex-col relative'
                      }`}
                    >
                      {viewMode === 'grid' ? (
                        <>
                          {/* Modern Grid View */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center space-x-4 flex-1 min-w-0">
                              <div className="relative">
                                <img 
                                  src={station.favicon || '/api/placeholder/64/64'} 
                                  alt=""
                                  className="w-16 h-16 rounded-xl bg-gray-200 dark:bg-gray-700 border-2 border-gray-200 dark:border-gray-600 flex-shrink-0"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                                {currentStation?.stationuuid === station.stationuuid && isPlaying && (
                                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                                    <span className="text-white text-xs">🎵</span>
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="font-bold text-lg line-clamp-2 mb-1">
                                  {station.name}
                                </h3>
                              </div>
                            </div>
                            
                            <button
                              onClick={() => toggleFavorite(station)}
                              className={`text-2xl transition-all duration-300 transform hover:scale-110 flex-shrink-0 ml-2 ${
                                isFavorite(station.stationuuid) ? 'text-yellow-400' : 'text-gray-400 hover:text-yellow-400'
                              }`}
                              title="Προσθήκη στα αγαπημένα"
                            >
                              ⭐
                            </button>
                          </div>
                          
                          <div className="text-sm opacity-70 space-y-2 flex-1">
                            <div className="flex items-center space-x-3">
                              <span className="bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-1 rounded-lg text-xs font-medium">
                                🌍 {station.country || 'Άγνωστο'}
                              </span>
                              <span className="bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 px-2 py-1 rounded-lg text-xs font-medium">
                                🗣️ {station.language || 'Άγνωστο'}
                              </span>
                            </div>
                            {station.tags && (
                              <p className="text-xs">
                                <span className="bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200 px-2 py-1 rounded-lg">
                                  🏷️ {station.tags.split(',').slice(0, 2).join(', ')}
                                </span>
                              </p>
                            )}
                            <div className="flex items-center space-x-3 mt-2">
                              <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-lg text-xs">
                                📊 {station.bitrate}kbps
                              </span>
                              <span className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-lg text-xs">
                                👆 {station.clickcount} κλικ
                              </span>
                            </div>
                          </div>

                          {/* Bottom Right Controls */}
                          <div className="absolute bottom-4 right-4 flex items-center space-x-3">
                            <button
                              onClick={() => openShareModal(station)}
                              className="text-gray-400 hover:text-blue-500 transition-all duration-300 p-2 hover:bg-blue-50 dark:hover:bg-blue-900 rounded-xl transform hover:scale-110"
                              title="Μοιραστείτε αυτόν τον σταθμό"
                            >
                              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.15c-.05.21-.08.43-.08.66 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                              </svg>
                            </button>
                            
                            <button
                              onClick={() => playStation(station)}
                              className={`w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 transform hover:scale-110 shadow-lg ${
                                currentStation?.stationuuid === station.stationuuid && isPlaying
                                  ? 'bg-red-600 hover:bg-red-700'
                                  : 'bg-green-600 hover:bg-green-700'
                              }`}
                            >
                              <span className="text-white text-xl">
                                {currentStation?.stationuuid === station.stationuuid && isPlaying ? '⏸️' : '▶️'}
                              </span>
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* Keep existing List View */}
                          <div className="flex items-start justify-between w-full">
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <img 
                                src={station.favicon || '/api/placeholder/64/64'} 
                                alt=""
                                className="w-12 h-12 rounded bg-gray-700 flex-shrink-0"
                                onError={(e) => e.target.style.display = 'none'}
                              />
                              <div className="flex-1 min-w-0">
                                <h3 className="font-semibold text-base truncate mb-1">
                                  {station.name}
                                </h3>
                                <div className="text-sm opacity-70 flex items-center space-x-4">
                                  <span>🌍 {station.country || 'Άγνωστο'}</span>
                                  <span>🗣️ {station.language || 'Άγνωστο'}</span>
                                  <span>📊 {station.bitrate}kbps</span>
                                  <span>👆 {station.clickcount} κλικ</span>
                                </div>
                              </div>
                            </div>
                            
                            <div className="flex items-center space-x-2 flex-shrink-0">
                              <button
                                onClick={() => toggleFavorite(station)}
                                className={`text-xl ${isFavorite(station.stationuuid) ? 'text-yellow-400' : 'text-gray-400 hover:text-yellow-400'} transition-colors`}
                                title="Προσθήκη στα αγαπημένα"
                              >
                                ⭐
                              </button>
                              
                              <button
                                onClick={() => openShareModal(station)}
                                className="text-gray-400 hover:text-blue-400 transition-colors"
                                title="Μοιραστείτε αυτόν τον σταθμό"
                              >
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.15c-.05.21-.08.43-.08.66 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                                </svg>
                              </button>
                              
                              <button
                                onClick={() => playStation(station)}
                                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                                  currentStation?.stationuuid === station.stationuuid && isPlaying
                                    ? 'bg-red-600 hover:bg-red-700'
                                    : 'bg-green-600 hover:bg-green-700'
                                }`}
                              >
                                <span className="text-white">
                                  {currentStation?.stationuuid === station.stationuuid && isPlaying ? '⏸️' : '▶️'}
                                </span>
                              </button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>

                {/* Modern Load More Button */}
                {!loading && hasMoreStations && displayStations.length > 0 && !showFavorites && (
                  <div className="text-center mt-12">
                    <button
                      onClick={loadMoreStations}
                      disabled={loadingMore}
                      className={`px-10 py-4 rounded-2xl font-medium transition-all duration-300 transform hover:scale-105 shadow-lg ${
                        loadingMore 
                          ? 'bg-gray-400 cursor-not-allowed text-white' 
                          : 'bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white'
                      }`}
                    >
                      {loadingMore ? (
                        <div className="flex items-center space-x-3">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                          <span>Φόρτωση περισσότερων...</span>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-3">
                          <span>Φόρτωση περισσότερων σταθμών</span>
                          <span className="text-lg">⬇️</span>
                        </div>
                      )}
                    </button>
                    <div className="mt-4 bg-gray-100 dark:bg-gray-800 rounded-xl p-4 max-w-md mx-auto">
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        📊 Εμφανίζονται <span className="font-bold text-blue-600">{displayStations.length}</span> από χιλιάδες διαθέσιμους σταθμούς
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}

            {!loading && displayStations.length === 0 && (
              <div className="text-center py-12">
                <p className="opacity-70 text-lg">Δεν βρέθηκαν σταθμοί. Δοκιμάστε να προσαρμόσετε τα φίλτρα σας.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Audio Element */}
      <audio
        ref={audioRef}
        onLoadStart={() => setLoading(true)}
        onCanPlay={() => setLoading(false)}
        onError={() => {
          setIsPlaying(false);
          setLoading(false);
          console.error('Audio playback error');
        }}
        onEnded={() => setIsPlaying(false)}
      />

      {/* DSGVO Cookie Banner */}
      {showCookieBanner && (
        <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white dark:bg-gray-900 border-t-4 border-blue-600 shadow-2xl">
          <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between space-y-4 md:space-y-0 md:space-x-6">
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                  🍪 Χρήση Cookies
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
                  Αυτή η ιστοσελίδα χρησιμοποιεί cookies για να βελτιώσει την εμπειρία σας. 
                  Χρησιμοποιούμε cookies για να αποθηκεύσουμε τις προτιμήσεις σας (αγαπημένους σταθμούς, θέμα) 
                  και να αναλύσουμε την κίνηση της ιστοσελίδας σύμφωνα με τον GDPR.
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Μπορείτε να διαβάσετε περισσότερα στην <a href="/privacy" className="underline text-blue-600 hover:text-blue-700">Πολιτική Απορρήτου</a> μας.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3 flex-shrink-0">
                <button
                  onClick={acceptCookies}
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-medium transition-all duration-300 text-sm"
                >
                  Αποδοχή Όλων
                </button>
                <button
                  onClick={acceptNecessaryOnly}
                  className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-all duration-300 text-sm"
                >
                  Μόνο Απαραίτητα
                </button>
                <button
                  onClick={rejectCookies}
                  className="px-6 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-xl font-medium transition-all duration-300 text-sm"
                >
                  Απόρριψη
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && shareStation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto border-2 ${
            theme === 'light'
              ? 'bg-white border-gray-200'
              : 'bg-gray-800 border-gray-600'
          }`}>
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold">Μοιραστείτε τον Σταθμό</h3>
              <button
                onClick={closeShareModal}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="mb-4 p-4 bg-gray-100 dark:bg-gray-700 rounded-xl">
              <div className="flex items-center space-x-3">
                <img 
                  src={shareStation.favicon || '/api/placeholder/48/48'} 
                  alt=""
                  className="w-12 h-12 rounded bg-gray-600"
                  onError={(e) => e.target.style.display = 'none'}
                />
                <div>
                  <h4 className="font-semibold">{shareStation.name}</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300">{shareStation.country} • {shareStation.language}</p>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-gray-600 dark:text-gray-300 mb-3">Επιλέξτε πλατφόρμα:</h4>
              
              {/* Social Media Buttons */}
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => shareToFacebook(shareStation)}
                  className="flex items-center justify-center space-x-2 p-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
                >
                  <span>📘</span>
                  <span>Facebook</span>
                </button>

                <button
                  onClick={() => shareToTwitter(shareStation)}
                  className="flex items-center justify-center space-x-2 p-3 bg-sky-500 hover:bg-sky-600 rounded-lg transition-colors"
                >
                  <span>🐦</span>
                  <span>Twitter</span>
                </button>

                <button
                  onClick={() => shareToWhatsApp(shareStation)}
                  className="flex items-center justify-center space-x-2 p-3 bg-green-600 hover:bg-green-700 rounded-lg transition-colors"
                >
                  <span>💬</span>
                  <span>WhatsApp</span>
                </button>

                <button
                  onClick={() => shareToTelegram(shareStation)}
                  className="flex items-center justify-center space-x-2 p-3 bg-blue-500 hover:bg-blue-600 rounded-lg transition-colors"
                >
                  <span>✈️</span>
                  <span>Telegram</span>
                </button>

                <button
                  onClick={() => shareToInstagram(shareStation)}
                  className="flex items-center justify-center space-x-2 p-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg transition-colors"
                >
                  <span>📷</span>
                  <span>Instagram</span>
                </button>

                <button
                  onClick={() => shareToTikTok(shareStation)}
                  className="flex items-center justify-center space-x-2 p-3 bg-black hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <span>📱</span>
                  <span>TikTok</span>
                </button>
              </div>

              {/* Direct Communication */}
              <div className="border-t border-gray-300 dark:border-gray-600 pt-3 mt-4">
                <h4 className="font-medium text-gray-600 dark:text-gray-300 mb-3">Άμεση επικοινωνία:</h4>
                <div className="grid grid-cols-1 gap-3">
                  <button
                    onClick={() => shareViaEmail(shareStation)}
                    className="flex items-center justify-center space-x-2 p-3 bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
                  >
                    <span>📧</span>
                    <span>Email</span>
                  </button>

                  <button
                    onClick={() => shareViaSMS(shareStation)}
                    className="flex items-center justify-center space-x-2 p-3 bg-green-700 hover:bg-green-800 rounded-lg transition-colors"
                  >
                    <span>💬</span>
                    <span>SMS</span>
                  </button>

                  <button
                    onClick={() => copyToClipboard(shareStation)}
                    className="flex items-center justify-center space-x-2 p-3 bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors"
                  >
                    <span>📋</span>
                    <span>Αντιγραφή Link</span>
                  </button>
                </div>
              </div>
            </div>

            <div className="mt-6 text-center">
              <button
                onClick={closeShareModal}
                className="px-6 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg transition-colors"
              >
                Κλείσιμο
              </button>
            </div>
          </div>
        </div>
      )}
        </>
      ) : (
        /* Privacy Policy View */
        <div className="min-h-screen py-8">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex justify-between items-center mb-6">
              <button
                onClick={() => setShowPrivacyPolicy(false)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
              >
                <span>←</span>
                <span>Επιστροφή στο Ράδιο</span>
              </button>
            </div>
            <div className={`rounded-2xl border-2 ${
              theme === 'light'
                ? 'bg-white border-gray-200'
                : 'bg-gray-800 border-gray-600'
            }`}>
              <PrivacyPolicyContent />
            </div>
          </div>
        </div>
      )}

      {/* Footer - Always visible */}
      {!showPrivacyPolicy && (
        <footer className={`border-t mt-12 py-6 ${
          theme === 'light'
            ? 'bg-white border-gray-200'
            : 'bg-gray-800 border-gray-600'
        }`}>
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="flex flex-col md:flex-row justify-center items-center space-y-2 md:space-y-0 md:space-x-6 text-sm text-gray-400">
              <p>© 2024 AI Agency M.IKE - Παγκόσμιο Ράδιο</p>
              <button
                onClick={() => setShowPrivacyPolicy(true)}
                className="hover:text-blue-400 transition-colors underline"
              >
                Πολιτική Απορρήτου
              </button>
              <span>|</span>
              <span>Θεσσαλονίκη, Ελλάδα</span>
              <span>|</span>
              <a
                href="mailto:info@ai-agency.gr"
                className="hover:text-blue-400 transition-colors"
              >
                info@ai-agency.gr
              </a>
              <a
                href="https://www.instagram.com/aipantheongr"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-blue-400 transition-colors"
              >
                @aipantheongr
              </a>
            </div>
            <div className="mt-2 text-xs text-gray-500">
              Αποποίηση ευθύνης: Δεν φέρουμε ευθύνη για το περιεχόμενο των ραδιοσταθμών
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

export default App;